import { useState, useEffect } from 'react';
import { stockService } from '../../services/stockService';
import type { Stock, CreateStockRequest, UpdateStockRequest } from '../../types/stock';
import { MIN_SEARCH_LENGTH } from '../../utils/searchConstants';

interface UseStockDataParams {
  companyId: number | null;
}

export function useStockData({ companyId }: UseStockDataParams) {
  // State
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterName, setFilterName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [stockToEdit, setStockToEdit] = useState<Stock | null>(null);

  // Debounce filter
  useEffect(() => {
    if (filterName.length > 0 && filterName.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => {
      setDebouncedName(filterName);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName]);

  // Fetch stocks
  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await stockService.search(companyId, {
          name: debouncedName || undefined,
          includeArchived: !showOnlyActive,
          page: page - 1,
          size: 20,
        });
        setStocks(response.content);
        setTotalPages(response.totalPages);

        setError(null);
      } catch (err) {
        console.error('Error fetching stocks:', err);
        setError('Не вдалося завантажити склади');
      } finally {
        setLoading(false);
        setIsFirstLoad(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, page, debouncedName, showOnlyActive]);

  // CRUD handlers
  const handleCreateStock = async (stockData: CreateStockRequest) => {
    if (!companyId) return;
    const newStock = await stockService.create(stockData);
    setStocks((prev) => [...prev, newStock]);
  };

  const handleDeleteStock = async (stockId: number) => {
    if (!window.confirm('Ви впевнені, що хочете архівувати цей склад?')) {
      return;
    }
    await stockService.delete(stockId);
    setStocks((prev) => (
      showOnlyActive
        ? prev.filter((s) => s.id !== stockId)
        : prev.map((s) => (s.id === stockId ? { ...s, deactivatedDate: new Date().toISOString() } : s))
    ));
  };

  const handleRestoreStock = async (stockId: number) => {
    await stockService.restore(stockId);
    setStocks((prev) => prev.map((s) => (
      s.id === stockId ? { ...s, deactivatedDate: undefined } : s
    )));
  };

  const handleEditStock = (stock: Stock) => {
    setStockToEdit(stock);
    setEditDialogOpen(true);
  };

  const handleUpdateStock = async (stockId: number, data: UpdateStockRequest) => {
    const updated = await stockService.update(stockId, data);
    setStocks((prev) => prev.map((s) => (s.id === stockId ? updated : s)));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleShowOnlyActiveChange = (value: boolean) => {
    setShowOnlyActive(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterName('');
    handleShowOnlyActiveChange(true);
  };

  return {
    data: {
      stocks,
      loading,
      isFirstLoad,
      error,
    },
    pagination: {
      page,
      count: totalPages,
      onChange: handlePageChange,
    },
    filters: {
      filterName,
      setFilterName,
      showFilters,
      setShowFilters,
      showOnlyActive,
      setShowOnlyActive: handleShowOnlyActiveChange,
      hasActiveFilters: !!debouncedName || !showOnlyActive,
      handleClearFilters,
    },
    crud: {
      handleCreateStock,
      handleDeleteStock,
      handleRestoreStock,
      handleEditStock,
      handleUpdateStock,
    },
    dialogs: {
      createDialogOpen,
      setCreateDialogOpen,
      editDialogOpen,
      setEditDialogOpen,
      stockToEdit,
    },
  };
}

